import { useState, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { CheckCircle, DollarSign, MapPin, User } from "lucide-react";
import { usePaymentMethods } from "@/hooks/usePaymentMethods";
import type { DeliveryOrderWithDetails } from "@/hooks/useDeliveryOrders";

interface DeliveryConfirmationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  order: DeliveryOrderWithDetails | null;
  onConfirm: (data: {
    orderId: string;
    paymentMethodId?: string;
    paymentAmount?: number;
    alreadyPaid: boolean;
  }) => void;
  isLoading?: boolean;
}

export function DeliveryConfirmationDialog({
  open,
  onOpenChange,
  order,
  onConfirm,
  isLoading,
}: DeliveryConfirmationDialogProps) {
  const { paymentMethods } = usePaymentMethods();
  const [paymentType, setPaymentType] = useState<"already_paid" | "collect">("already_paid");
  const [selectedMethodId, setSelectedMethodId] = useState<string>("");
  const [receivedAmount, setReceivedAmount] = useState<string>("");

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  };

  const totalPaid = useMemo(() => {
    return order?.payments?.reduce((sum, p) => sum + Number(p.amount), 0) || 0;
  }, [order]);

  const orderTotal = Number(order?.total || 0);
  const remainingAmount = orderTotal - totalPaid;
  const isAlreadyFullyPaid = remainingAmount <= 0;

  const parsedReceived = parseFloat(receivedAmount.replace(",", ".")) || 0;
  const change = parsedReceived - remainingAmount;

  const handleConfirm = () => {
    if (!order) return;

    if (paymentType === "already_paid" || isAlreadyFullyPaid) {
      onConfirm({
        orderId: order.id,
        alreadyPaid: true,
      });
    } else {
      onConfirm({
        orderId: order.id,
        paymentMethodId: selectedMethodId,
        paymentAmount: remainingAmount,
        alreadyPaid: false,
      });
    }
    onOpenChange(false);
  };

  // Reset state when dialog opens with new order
  const handleOpenChange = (open: boolean) => {
    if (open) {
      setPaymentType(isAlreadyFullyPaid ? "already_paid" : "collect");
      setSelectedMethodId(paymentMethods[0]?.id || "");
      setReceivedAmount("");
    }
    onOpenChange(open);
  };

  if (!order) return null;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-green-600" />
            Confirmar Entrega - Pedido #{order.order_number}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Customer Info */}
          <div className="bg-muted/50 rounded-lg p-3 space-y-2">
            <div className="flex items-center gap-2 text-sm">
              <User className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium">{order.customer_name || "Cliente"}</span>
            </div>
            <div className="flex items-start gap-2 text-sm text-muted-foreground">
              <MapPin className="h-4 w-4 mt-0.5 shrink-0" />
              <span>{order.delivery_address}</span>
            </div>
          </div>

          {/* Order Total */}
          <div className="flex items-center justify-between p-3 bg-primary/5 rounded-lg">
            <span className="font-medium">Total do Pedido</span>
            <span className="text-xl font-bold text-primary">
              {formatCurrency(orderTotal)}
            </span>
          </div>

          {/* Payment status */}
          {totalPaid > 0 && (
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Já pago</span>
              <Badge variant="outline" className="text-green-600 border-green-600">
                {formatCurrency(totalPaid)}
              </Badge>
            </div>
          )}

          {remainingAmount > 0 && (
            <div className="flex items-center justify-between text-sm font-medium">
              <span>A receber</span>
              <span className="text-amber-600">{formatCurrency(remainingAmount)}</span>
            </div>
          )}

          <Separator />

          {/* Payment Type Selection */}
          {!isAlreadyFullyPaid && (
            <div className="space-y-3">
              <Label>Pagamento</Label>
              <RadioGroup
                value={paymentType}
                onValueChange={(v) => setPaymentType(v as "already_paid" | "collect")}
                className="space-y-2"
              >
                <div className="flex items-center space-x-2 rounded-lg border p-3">
                  <RadioGroupItem value="already_paid" id="already_paid" />
                  <Label htmlFor="already_paid" className="cursor-pointer flex-1">
                    Já pago (PIX/Online)
                  </Label>
                </div>
                <div className="flex items-center space-x-2 rounded-lg border p-3">
                  <RadioGroupItem value="collect" id="collect" />
                  <Label htmlFor="collect" className="cursor-pointer flex-1">
                    Cobrar na entrega
                  </Label>
                </div>
              </RadioGroup>
            </div>
          )}

          {/* Payment Method Selection */}
          {paymentType === "collect" && !isAlreadyFullyPaid && (
            <div className="space-y-3">
              <Label>Forma de pagamento</Label>
              <div className="flex flex-wrap gap-2">
                {paymentMethods
                  .filter((pm) => pm.is_active)
                  .map((pm) => (
                    <Button
                      key={pm.id}
                      type="button"
                      variant={selectedMethodId === pm.id ? "default" : "outline"}
                      size="sm"
                      onClick={() => setSelectedMethodId(pm.id)}
                    >
                      {pm.name}
                    </Button>
                  ))}
              </div>

              {/* Cash change calculation */}
              {paymentMethods.find((pm) => pm.id === selectedMethodId)?.type === "cash" && (
                <div className="space-y-2">
                  <Label>Valor recebido</Label>
                  <div className="flex items-center gap-2">
                    <DollarSign className="h-4 w-4 text-muted-foreground" />
                    <Input
                      type="text"
                      placeholder={formatCurrency(remainingAmount)}
                      value={receivedAmount}
                      onChange={(e) => setReceivedAmount(e.target.value)}
                    />
                  </div>
                  {parsedReceived > 0 && (
                    <div className="flex items-center justify-between text-sm bg-muted/50 p-2 rounded">
                      <span>Troco</span>
                      <span className={change >= 0 ? "text-green-600 font-medium" : "text-destructive"}>
                        {formatCurrency(Math.max(0, change))}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={
              isLoading ||
              (paymentType === "collect" && !isAlreadyFullyPaid && !selectedMethodId)
            }
            className="bg-green-600 hover:bg-green-700"
          >
            <CheckCircle className="h-4 w-4 mr-2" />
            Confirmar Entrega
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
